import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { GitCompare, ArrowRight, RotateCcw, CheckCircle2, Wand2 } from "lucide-react";

// Format a parameter value for human-friendly display (Italian).
const fmtVal = (param, v) => {
  if (param.type === "bool") return v ? "Sì" : "No";
  if (v === "" || v === null || v === undefined) return "—";
  return String(v);
};

// Determine whether the current value differs from the preset value.
const isDifferent = (param, current, preset) => {
  if (param.type === "bool") return Boolean(current) !== Boolean(preset);
  if (param.type === "int" || param.type === "double") {
    const a = current === "" || current === null || current === undefined ? null : Number(current);
    const b = preset === "" || preset === null || preset === undefined ? null : Number(preset);
    return a !== b;
  }
  return String(current ?? "") !== String(preset ?? "");
};

export const PresetDiffDialog = ({
  open,
  onOpenChange,
  presets = [],
  meta,
  config,
  activeId,
  onApplyPreset,
  onRevertParam,
}) => {
  const fallbackId = activeId || (presets[0] && presets[0].id) || null;
  const [selectedId, setSelectedId] = useState(fallbackId);

  // Keep the selected preset in sync with the active preset when reopening.
  useEffect(() => {
    if (open) setSelectedId(activeId || (presets[0] && presets[0].id) || null);
  }, [open, activeId, presets]);

  const selectedPreset = useMemo(
    () => presets.find((p) => p.id === selectedId) || null,
    [presets, selectedId]
  );

  // Build the grouped diff: only parameters whose value differs.
  const { groupedDiff, totalDiff } = useMemo(() => {
    const grouped = [];
    let total = 0;
    if (!meta || !selectedPreset || !config) return { groupedDiff: grouped, totalDiff: 0 };
    const presetConfig = selectedPreset.config || {};
    for (const group of meta.groups) {
      const rows = [];
      for (const param of meta.parameters.filter((p) => p.group === group)) {
        const cur = config[param.key];
        const pre = presetConfig[param.key];
        if (isDifferent(param, cur, pre)) {
          rows.push({ param, current: cur, preset: pre });
          total += 1;
        }
      }
      if (rows.length) grouped.push({ group, rows });
    }
    return { groupedDiff: grouped, totalDiff: total };
  }, [meta, selectedPreset, config]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-card border-border"
        data-testid="preset-diff-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 uppercase tracking-[-0.01em]">
            <GitCompare className="h-5 w-5 text-[hsl(var(--primary))]" />
            Confronta con preset
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Differenze tra la <span className="text-[hsl(var(--primary))]">configurazione corrente</span> e un preset di riferimento.
          </DialogDescription>
        </DialogHeader>

        {/* Preset selector + counter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-[220px]">
            <Select value={selectedId || undefined} onValueChange={setSelectedId}>
              <SelectTrigger data-testid="diff-preset-select">
                <SelectValue placeholder="Seleziona preset" />
              </SelectTrigger>
              <SelectContent>
                {presets.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Badge
            data-testid="diff-count"
            variant="secondary"
            className={cn(
              "font-mono text-xs",
              totalDiff > 0
                ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30"
                : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            )}
          >
            {totalDiff === 0 ? "Nessuna differenza" : `${totalDiff} ${totalDiff === 1 ? "differenza" : "differenze"}`}
          </Badge>
        </div>

        {/* Diff body */}
        <ScrollArea className="max-h-[52vh] pr-3 thin-scroll">
          {totalDiff === 0 ? (
            <div
              className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground"
              data-testid="diff-empty"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-400 opacity-80" />
              <p className="text-sm">
                La configurazione corrente coincide con il preset
                {selectedPreset ? ` “${selectedPreset.name}”` : ""}.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedDiff.map(({ group, rows }) => (
                <div key={group} data-testid={`diff-group-${group}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">
                      {group}
                    </span>
                    <Badge variant="secondary" className="text-[10px] md:text-xs font-mono">{rows.length}</Badge>
                  </div>
                  <div className="rounded-md border border-border divide-y divide-border/70 overflow-hidden">
                    {rows.map(({ param, current, preset }) => (
                      <div
                        key={param.key}
                        data-testid={`diff-row-${param.key}`}
                        className="flex items-center justify-between gap-3 px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium leading-tight truncate">{param.label}</div>
                          <div className="mt-1 flex items-center gap-2 font-mono text-xs">
                            <span
                              data-testid={`diff-preset-value-${param.key}`}
                              className="text-muted-foreground line-through decoration-muted-foreground/50"
                            >
                              {fmtVal(param, preset)}
                            </span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span
                              data-testid={`diff-current-value-${param.key}`}
                              className="text-[hsl(var(--primary))] font-semibold"
                            >
                              {fmtVal(param, current)}
                            </span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid={`diff-revert-${param.key}`}
                          className="shrink-0 text-xs text-muted-foreground hover:text-[hsl(var(--primary))]"
                          onClick={() => onRevertParam && onRevertParam(param.key, preset)}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1" />
                          Ripristina
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="diff-apply-preset-button"
            disabled={!selectedPreset || totalDiff === 0}
            onClick={() => selectedPreset && onApplyPreset && onApplyPreset(selectedPreset)}
          >
            <Wand2 className="h-4 w-4 mr-2" />
            {selectedPreset ? `Applica “${selectedPreset.name}”` : "Applica preset"}
          </Button>
          <Button
            type="button"
            data-testid="diff-close-button"
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
            onClick={() => onOpenChange(false)}
          >
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
